import { NetworkError, classifyFetchError } from './errors.js';
const REQUEST_TIMEOUT_MS = 3_000;
function buildUrl(seed, logicalPath) {
    const prefix = seed.pathPrefix ?? '';
    const path = logicalPath.startsWith('/') ? logicalPath : `/${logicalPath}`;
    return `${seed.url}${prefix}${path}`;
}
function sortedByHealth(states) {
    return [...states].sort((a, b) => {
        const aHealthy = a.errorCount === 0 ? 0 : 1;
        const bHealthy = b.errorCount === 0 ? 0 : 1;
        if (aHealthy !== bHealthy)
            return aHealthy - bHealthy;
        return a.latencyMs - b.latencyMs;
    });
}
export function createRpcClient(seeds) {
    if (seeds.length === 0) {
        throw new Error('createRpcClient requires at least one seed');
    }
    const states = seeds.map((seed) => ({
        seed,
        latencyMs: 0,
        errorCount: 0,
        lastError: null,
    }));
    async function tryRequest(method, path, body, userSignal) {
        const ordered = sortedByHealth(states);
        let lastError = null;
        for (const state of ordered) {
            const url = buildUrl(state.seed, path);
            const ctrl = new AbortController();
            const timeout = setTimeout(() => ctrl.abort(), REQUEST_TIMEOUT_MS);
            if (userSignal) {
                if (userSignal.aborted) {
                    ctrl.abort();
                }
                else {
                    userSignal.addEventListener('abort', () => ctrl.abort(), { once: true });
                }
            }
            const started = Date.now();
            try {
                const init = {
                    method,
                    signal: ctrl.signal,
                    headers: { Accept: 'application/json' },
                };
                if (method === 'POST') {
                    init.body = JSON.stringify(body);
                    init.headers = {
                        ...init.headers,
                        'Content-Type': 'application/json',
                    };
                }
                const res = await fetch(url, init);
                const elapsed = Date.now() - started;
                if (!res.ok) {
                    state.errorCount += 1;
                    state.lastError = `HTTP ${res.status}`;
                    lastError = new NetworkError('ALL_NODES_DOWN', 'errors.network.allNodesDown', true, `HTTP ${res.status} from ${state.seed.label}`);
                    continue;
                }
                const json = (await res.json());
                state.latencyMs = elapsed;
                state.errorCount = 0;
                state.lastError = null;
                return json;
            }
            catch (err) {
                const elapsed = Date.now() - started;
                state.errorCount += 1;
                state.lastError = err instanceof Error ? err.message : String(err);
                lastError = classifyFetchError(err, elapsed);
            }
            finally {
                clearTimeout(timeout);
            }
        }
        throw (lastError ??
            new NetworkError('ALL_NODES_DOWN', 'errors.network.allNodesDown', true, 'no seeds available'));
    }
    return {
        getApi(path, signal) {
            return tryRequest('GET', path, undefined, signal);
        },
        postApi(path, body, signal) {
            return tryRequest('POST', path, body, signal);
        },
        getActiveSeeds() {
            return states.map((s) => ({
                seed: s.seed,
                latencyMs: s.latencyMs,
                errorCount: s.errorCount,
                lastError: s.lastError,
            }));
        },
    };
}
//# sourceMappingURL=client.js.map